/**
 * Australian personal + partnership tax math (pure, framework-free, FY-keyed).
 *
 * This is the single source of truth for the numbers on the Tax & GST view.
 * Everything here is a pure function of its inputs so it can be unit-tested
 * (see auTax.test.ts) — no Firestore, no React.
 *
 * Scope + caveats (mirrored in the UI disclaimer):
 *   - Resident individuals only. Estimates income tax + Medicare levy − LITO.
 *   - Cash basis: figures come from bank movements, filtered by transaction
 *     date into the 1-Jul→30-Jun financial year.
 *   - Does NOT model: HELP/HECS, Medicare levy surcharge, private health
 *     rebate, depreciation/capital allowances, super contributions, CGT,
 *     franking credits, or the family Medicare reduction beyond the standard
 *     shade-in approximation. Confirm with a registered tax agent.
 *
 * Rates verified against the ATO for 2025-26 (and 2024-25), including the
 * 2.9% Budget-2026 uplift to the Medicare levy low-income thresholds.
 */

export type FinancialYear = '2024-25' | '2025-26';

/** Most-recent first — drives the year selector default. */
export const FINANCIAL_YEARS: FinancialYear[] = ['2025-26', '2024-25'];

export const GST_DIVISOR = 11; // GST-inclusive amount ÷ 11 = GST component
export const MEDICARE_RATE = 0.02;

/** A marginal bracket: `rate` applies to the slice of taxable income above
 *  `from` (and below the next bracket's `from`). */
export interface TaxBracket {
  from: number;
  rate: number;
}

export interface MedicareConfig {
  rate: number;
  /** Below `singleLower` a single person pays nothing; the levy shades in at
   *  `phaseInRate` per $1 up to `singleUpper` (= singleLower × 1.25), where
   *  the full `rate` applies. Family thresholds work the same way on combined
   *  income, plus `perChild` added to the lower threshold per dependant. */
  singleLower: number;
  singleUpper: number;
  familyLower: number;
  familyUpper: number;
  perChild: number;
  phaseInRate: number;
}

export interface LitoConfig {
  max: number;
  fullUpTo: number; // full offset at/below this income
  band1To: number;  // taper 1 ends here
  band1Taper: number;
  band1Floor: number; // offset remaining at band1To
  band2To: number;  // offset reaches zero here
  band2Taper: number;
}

export interface YearTax {
  brackets: TaxBracket[];
  medicare: MedicareConfig;
  lito: LitoConfig;
}

/** Stage-3 brackets — identical for 2024-25 and 2025-26. */
const STAGE3_BRACKETS: TaxBracket[] = [
  { from: 0, rate: 0 },
  { from: 18200, rate: 0.16 },
  { from: 45000, rate: 0.3 },
  { from: 135000, rate: 0.37 },
  { from: 190000, rate: 0.45 },
];

/** LITO formula is unchanged across these years. */
const LITO: LitoConfig = {
  max: 700,
  fullUpTo: 37500,
  band1To: 45000,
  band1Taper: 0.05,
  band1Floor: 325,
  band2To: 66667,
  band2Taper: 0.015,
};

export const TAX_YEARS: Record<FinancialYear, YearTax> = {
  '2025-26': {
    brackets: STAGE3_BRACKETS,
    // 2.9% Budget-2026 uplift, retroactive to 1 Jul 2025.
    medicare: {
      rate: MEDICARE_RATE,
      singleLower: 28011,
      singleUpper: 35013,
      familyLower: 47238,
      familyUpper: 59047,
      perChild: 4338,
      phaseInRate: 0.1,
    },
    lito: LITO,
  },
  '2024-25': {
    brackets: STAGE3_BRACKETS,
    medicare: {
      rate: MEDICARE_RATE,
      singleLower: 27222,
      singleUpper: 34027,
      familyLower: 45907,
      familyUpper: 57383,
      perChild: 4216,
      phaseInRate: 0.1,
    },
    lito: LITO,
  },
};

/** Round to whole cents, killing float residue (matches money.ts). */
export function roundMoney(v: number): number {
  return Math.round((Number(v) || 0) * 100) / 100;
}

// ---------------------------------------------------------------------------
// Financial year helpers
// ---------------------------------------------------------------------------

/** ISO date range (inclusive) for a financial year, for cash-basis filtering. */
export function financialYearRange(fy: FinancialYear): { start: string; end: string } {
  const startYear = parseInt(fy.slice(0, 4), 10);
  return { start: `${startYear}-07-01`, end: `${startYear + 1}-06-30` };
}

export function financialYearLabel(fy: FinancialYear): string {
  const startYear = parseInt(fy.slice(0, 4), 10);
  return `1 Jul ${startYear} – 30 Jun ${startYear + 1}`;
}

// ---------------------------------------------------------------------------
// GST
// ---------------------------------------------------------------------------

/** GST component of a GST-inclusive amount (sign-agnostic, always ≥ 0). */
export function gstFromInclusive(amountIncl: number): number {
  return roundMoney(Math.abs(Number(amountIncl) || 0) / GST_DIVISOR);
}

/** GST-exclusive value of a GST-inclusive amount. When `gstApplicable` is
 *  false (input-taxed / GST-free / not registered) the amount is returned
 *  unchanged. Sign is preserved. */
export function exGst(amountIncl: number, gstApplicable: boolean): number {
  const a = Number(amountIncl) || 0;
  return roundMoney(gstApplicable ? a - a / GST_DIVISOR : a);
}

// ---------------------------------------------------------------------------
// Income tax
// ---------------------------------------------------------------------------

/** Gross income tax before offsets/levies, at resident marginal rates. */
export function incomeTax(taxable: number, fy: FinancialYear): number {
  const t = Math.max(0, Number(taxable) || 0);
  const { brackets } = TAX_YEARS[fy];
  let tax = 0;
  for (let i = 0; i < brackets.length; i++) {
    const from = brackets[i].from;
    const to = i + 1 < brackets.length ? brackets[i + 1].from : Infinity;
    if (t > from) tax += (Math.min(t, to) - from) * brackets[i].rate;
  }
  return roundMoney(tax);
}

/** Marginal income-tax rate (bracket only, excludes Medicare) at `taxable`. */
export function marginalIncomeRate(taxable: number, fy: FinancialYear): number {
  const t = Math.max(0, Number(taxable) || 0);
  const { brackets } = TAX_YEARS[fy];
  let rate = 0;
  for (const b of brackets) if (t > b.from) rate = b.rate;
  return rate;
}

/** Low Income Tax Offset — automatic, non-refundable, reduces income tax
 *  (not Medicare). */
export function lito(taxable: number, fy: FinancialYear): number {
  const t = Math.max(0, Number(taxable) || 0);
  const l = TAX_YEARS[fy].lito;
  if (t <= l.fullUpTo) return l.max;
  if (t <= l.band1To) return roundMoney(l.max - (t - l.fullUpTo) * l.band1Taper);
  if (t <= l.band2To) return roundMoney(Math.max(0, l.band1Floor - (t - l.band1To) * l.band2Taper));
  return 0;
}

// ---------------------------------------------------------------------------
// Medicare levy
// ---------------------------------------------------------------------------

export interface MedicareOpts {
  /** Combined taxable income of the couple. When provided, the family
   *  low-income threshold is used (correct for a married partner). Omit for a
   *  single-person calculation. */
  familyTaxableIncome?: number;
  /** Dependent children — each lifts the family lower threshold by `perChild`. */
  dependents?: number;
}

/**
 * Medicare levy for one person.
 *
 * Above the relevant upper threshold this is a flat 2% of the person's own
 * taxable income. In the shade-in band it phases in at 10c/$1. For a couple we
 * apportion the family shade-in levy by the person's share of family income —
 * a documented approximation of the ATO family reduction that is exact at and
 * above the upper threshold (the near-certain case for a trading partnership).
 */
export function medicareLevy(taxable: number, fy: FinancialYear, opts: MedicareOpts = {}): number {
  const t = Math.max(0, Number(taxable) || 0);
  const m = TAX_YEARS[fy].medicare;

  if (opts.familyTaxableIncome != null) {
    const lower = m.familyLower + Math.max(0, opts.dependents || 0) * m.perChild;
    const upper = lower * 1.25;
    const fam = Math.max(0, opts.familyTaxableIncome);
    if (fam <= lower) return 0;
    if (fam >= upper) return roundMoney(t * m.rate);
    const familyLevy = m.phaseInRate * (fam - lower);
    const share = fam > 0 ? t / fam : 0;
    return roundMoney(familyLevy * share);
  }

  if (t <= m.singleLower) return 0;
  if (t >= m.singleUpper) return roundMoney(t * m.rate);
  return roundMoney(m.phaseInRate * (t - m.singleLower));
}

// ---------------------------------------------------------------------------
// Full individual estimate
// ---------------------------------------------------------------------------

export interface IndividualTaxInput {
  taxable: number;
  fy: FinancialYear;
  familyTaxableIncome?: number;
  dependents?: number;
}

export interface IndividualTaxResult {
  taxable: number;
  incomeTax: number;
  lito: number;
  /** incomeTax − LITO, floored at 0 (LITO can't create a refund). */
  incomeTaxAfterOffset: number;
  medicare: number;
  totalTax: number;
  afterTax: number;
  /** totalTax ÷ taxable (0 when taxable is 0). */
  averageRate: number;
  /** Bracket marginal rate + 2% when the Medicare levy is in full effect. */
  marginalRate: number;
}

export function estimateIndividualTax(input: IndividualTaxInput): IndividualTaxResult {
  const { fy } = input;
  const taxable = Math.max(0, Number(input.taxable) || 0);
  const gross = incomeTax(taxable, fy);
  const offset = lito(taxable, fy);
  const incomeTaxAfterOffset = Math.max(0, roundMoney(gross - offset));
  const medicare = medicareLevy(taxable, fy, {
    familyTaxableIncome: input.familyTaxableIncome,
    dependents: input.dependents,
  });
  const totalTax = roundMoney(incomeTaxAfterOffset + medicare);
  const afterTax = roundMoney(taxable - totalTax);
  const m = TAX_YEARS[fy].medicare;
  const medicareFull =
    input.familyTaxableIncome != null
      ? Math.max(0, input.familyTaxableIncome) >=
        (m.familyLower + Math.max(0, input.dependents || 0) * m.perChild) * 1.25
      : taxable >= m.singleUpper;
  return {
    taxable,
    incomeTax: gross,
    lito: offset,
    incomeTaxAfterOffset,
    medicare,
    totalTax,
    afterTax,
    averageRate: taxable > 0 ? totalTax / taxable : 0,
    marginalRate: marginalIncomeRate(taxable, fy) + (medicareFull ? MEDICARE_RATE : 0),
  };
}

// ---------------------------------------------------------------------------
// Capital allowances (depreciation) — Div 40 assets + Div 43 capital works
// ---------------------------------------------------------------------------

/** Car depreciation cost limit by FY — a car's cost base is capped here.
 *  2024-25 and 2025-26 are both $69,674 (unchanged). */
export const CAR_LIMIT: Record<FinancialYear, number> = { '2024-25': 69674, '2025-26': 69674 };

/** Instant asset write-off threshold by FY (per asset, GST-exclusive; small
 *  business turnover < $10m). $20,000 for both years. */
export const INSTANT_ASSET_WRITEOFF: Record<FinancialYear, number> = { '2024-25': 20000, '2025-26': 20000 };

/** Concessional (before-tax) super contributions cap by FY — the most you can
 *  claim as a personal super deduction in a year (before any carry-forward of
 *  unused cap from the prior 5 years, available if total super balance < $500k).
 *  $30,000 for both 2024-25 and 2025-26. */
export const SUPER_CONCESSIONAL_CAP: Record<FinancialYear, number> = { '2024-25': 30000, '2025-26': 30000 };

export const DEPRECIATION_METHODS = [
  'capitalWorks',      // Div 43 building works — 2.5%/yr over 40 years
  'immediate',         // instant asset write-off in the acquisition year
  'pool',              // small business simplified pool — 15% yr1, 30% after
  'primeCost',         // Div 40 straight line — cost/effective-life per year
  'diminishingValue',  // Div 40 diminishing value — 200%/effective-life
] as const;
export type DepreciationMethod = typeof DEPRECIATION_METHODS[number];

export const DEPRECIATION_METHOD_LABELS: Record<DepreciationMethod, string> = {
  capitalWorks: 'Capital works (building, 2.5%/yr)',
  immediate: 'Instant write-off (< $20k, year 1)',
  pool: 'Small-business pool (15% then 30%)',
  primeCost: 'Prime cost (straight line)',
  diminishingValue: 'Diminishing value (200%/life)',
};

export interface CapitalAsset {
  id: string;
  label: string;
  method: DepreciationMethod;
  /** GST-exclusive cost of the whole asset. */
  cost: number;
  /** Business-use fraction, 0..1. */
  businessUsePct: number;
  acquiredFY: FinancialYear;
  /** Effective life (years) — used by primeCost / diminishingValue. */
  effectiveLifeYears?: number;
  /** Car → cost base capped at CAR_LIMIT for the acquisition year. */
  isCar?: boolean;
}

const fyStartYear = (fy: FinancialYear) => parseInt(fy.slice(0, 4), 10);

/**
 * Deduction for one capital asset in the selected financial year. Returns 0
 * before acquisition or once the write-off period has ended. Business-use% is
 * applied to the deduction (for `pool` it enters the pool base up-front).
 */
export function capitalAllowanceForYear(asset: CapitalAsset, fy: FinancialYear): number {
  const buPct = Math.min(1, Math.max(0, Number(asset.businessUsePct) || 0));
  const cost = Math.max(0, Number(asset.cost) || 0);
  const yearsSince = fyStartYear(fy) - fyStartYear(asset.acquiredFY);
  if (yearsSince < 0) return 0;
  // A car's cost base is capped at the car limit for its acquisition year.
  const cappedCost = asset.isCar ? Math.min(cost, CAR_LIMIT[asset.acquiredFY] ?? cost) : cost;

  switch (asset.method) {
    case 'capitalWorks':
      return yearsSince < 40 ? roundMoney(cappedCost * buPct * 0.025) : 0;
    case 'immediate':
      // Instant asset write-off only applies to assets costing LESS than the
      // year's threshold (per asset, GST-exclusive). A ≥-threshold asset is
      // ineligible → 0 here (the UI warns to use pool/depreciation instead).
      if (cost >= (INSTANT_ASSET_WRITEOFF[asset.acquiredFY] ?? Infinity)) return 0;
      return yearsSince === 0 ? roundMoney(cappedCost * buPct) : 0;
    case 'pool': {
      // Small-business pool: 15% in the year the (business portion of the)
      // asset enters, 30% of the opening balance thereafter. Low-pool-value
      // rule (s328-210): whenever the balance — INCLUDING the current-year
      // addition — is below the instant-write-off threshold, the whole pool
      // is written off that year (so a sub-threshold pool clears in year 1).
      const threshold = INSTANT_ASSET_WRITEOFF[fy] ?? 0;
      let bal = cappedCost * buPct;
      let ded = 0;
      for (let i = 0; i <= yearsSince; i++) {
        if (bal <= 0) { ded = 0; break; }
        if (bal < threshold) {
          ded = bal; // low pool value — write off the remaining balance
          bal = 0;
        } else {
          ded = bal * (i === 0 ? 0.15 : 0.3);
          bal -= ded;
        }
      }
      return roundMoney(ded);
    }
    case 'primeCost': {
      const life = Math.max(1, Number(asset.effectiveLifeYears) || 1);
      return yearsSince < life ? roundMoney(cappedCost * buPct * (1 / life)) : 0;
    }
    case 'diminishingValue': {
      const life = Math.max(1, Number(asset.effectiveLifeYears) || 1);
      const rate = 2 / life;
      let av = cappedCost;
      let ded = 0;
      for (let i = 0; i <= yearsSince; i++) {
        const decline = av * rate;
        ded = decline * buPct;
        av -= decline;
      }
      return roundMoney(ded);
    }
    default:
      return 0;
  }
}

export function totalCapitalAllowances(assets: CapitalAsset[], fy: FinancialYear): number {
  return roundMoney((assets || []).reduce((s, a) => s + capitalAllowanceForYear(a, fy), 0));
}
